import type React from 'react';

import { IconCross } from '@xingternal/icons';
import { SingleSelect } from '@xingternal/menu';
import { usePopOver } from '@xingternal/pop-over';
import { BodyCopy } from '@xingternal/typography';

import {
  StyledMenuHeadline,
  StyledTag,
  StyledMenu,
  StyledMoreOptions,
} from './dropdown-tag.styles';

type DropdownTagOption = {
  copy: string;
  value: string;
};

export type DropdownTagProps = {
  className?: string;
  options: DropdownTagOption[];
  menuHeaderCopy?: React.ReactNode;
  moreOptionsCopy?: React.ReactNode;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  value?: string;
  onChange?: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onMoreOptionsClick?: () => void;
};

export const DropdownTag: React.FC<
  React.PropsWithChildren<DropdownTagProps>
> = ({
  className,
  children,
  options,
  icon,
  menuHeaderCopy,
  moreOptionsCopy,
  value,
  onChange,
  onOpen,
  onClose,
  onMoreOptionsClick,
}) => {
  const menu = usePopOver();

  const openMenu = (): void => {
    onOpen && onOpen();
    menu.handleShow();
  };

  const closeMenu = (): void => {
    onClose && onClose();
    menu.handleHide();
  };

  const toggleMenu = (): void => {
    if (menu.show) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const onChangeHelper = (option: DropdownTagOption | null): void => {
    onChange && onChange(option?.value || '');
    menu.handleHide();
  };

  const copy = options.find((option) => option.value === value)?.copy;

  return (
    <span className={className}>
      <StyledTag
        leadingIcon={icon || undefined}
        trailingIcon={
          value
            ? () => (
                <IconCross
                  data-qa="clear-value"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChangeHelper(null);
                  }}
                />
              )
            : undefined
        }
        selected={menu.show || !!copy}
        onClick={toggleMenu}
        ref={menu.triggerRef}>
        <BodyCopy noMargin>{copy || children}</BodyCopy>
      </StyledTag>

      <StyledMenu
        isInFlow
        dimmerTitle="close menu"
        onOutsideClick={closeMenu}
        show={menu.show}
        triggerRef={menu.triggerRef}>
        <StyledMenuHeadline fontWeight="bold" lineHeight="dense">
          {menuHeaderCopy}
        </StyledMenuHeadline>

        {options.map((option) => {
          return (
            <SingleSelect
              key={option.value}
              selected={option.value === value}
              onClick={() => onChangeHelper(option)}>
              {option.copy}
            </SingleSelect>
          );
        })}

        {moreOptionsCopy ? (
          <StyledMoreOptions
            onClick={() => {
              onMoreOptionsClick && onMoreOptionsClick();
              closeMenu();
            }}>
            {moreOptionsCopy}
          </StyledMoreOptions>
        ) : null}
      </StyledMenu>
    </span>
  );
};
